/*
 *
 * PayToCardPopup
 *
 * @version 1.0.0
 * @author Davide Polci
 * @since 02-Oct-2026
 */

import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import MerchantModal from '@nexidigital/merchantmodal'
import PortalSelect from '@nexidigital/portalselect'
import PortalSwitch from '@nexidigital/portalswitch'
import { isEqual } from 'lodash'
import { readOnlyControl } from 'Utility/globalMethods'
import PortalTextBlocked from 'Components/PortalTextBlocked'
import useManageVasForm from 'Hooks/useManageVasForm'
import formattedMessage, { captions } from './languageModule'
import './PayToCardPopup.scss'

const PayToCardPopup = ({ data, isUpdatingVas, actions, userPermissions }) => {
  const { vas } = data
  const intl = useIntl()
  const matchLevelSelectRef = useRef(null)

  const vasParams = vas?.params || []
  const subscriptionParamsByName = vasParams.reduce((acc, param) => {
    acc[param.featureParam.name] = param.featureParam.id
    return acc
  }, {})

  const subscriptionParamsInitial = vasParams.reduce((acc, param) => {
    acc[param.featureParam.name] = param.value ?? ''
    return acc
  }, {})
  const formik = useManageVasForm({
    onSubmit: (values) => {
      const payload = {
        subscriptionParams: Object.entries(values.subscriptionParams || {})
          .filter(([name]) => name === 'PayToCardNameMatchScore')
          .map(([name]) => ({
            featureParam: { id: subscriptionParamsByName[name] ?? vas?.feature?.id, name },
            value: value || null
          }))
      }
      actions.doPostSubscriptionGlobal({ values, vas, payload })
    },
    initialValues: { status: vas?.status || 'Disabled', subscriptionParams: subscriptionParamsInitial }
  })
  
  const isReadOnlyUser = readOnlyControl('settings.terminals.vas.payToCard', userPermissions)

  return (
    <div className='payToCardPopup_container'>
      <MerchantModal
        closeLabel={intl.formatMessage(captions.close)}
        topBarTitle={formattedMessage.titleManage}
        onClose={() => actions.closePopup()}
        primaryButtonLabel={formattedMessage.confirm}
        primaryAction={() => formik.submitForm()}
        primaryButtonDisabled={!formik.isValid || formik.isSubmitting || (isEqual(formik.values, formik.initialValues)) || isReadOnlyUser}
        primaryButtonLoading={isUpdatingVas}
        secondaryButtonLabel={intl.formatMessage(captions.back)}
        secondaryAction={() => actions.closePopup()}>
        <div className='payToCardPopup_content'>
          <MerchantGenericCard>
            <div className='payToCardPopup_service'>
              <span>{formattedMessage.serviceName}</span>
              <PortalSwitch
                id='payToCard_status'
                ariaLabel={intl.formatMessage(captions.status)}
                checked={formik.values.status === 'Enabled'}
                activeLabel={intl.formatMessage(captions.statusLabel_Enabled)}
                deactiveLabel={intl.formatMessage(captions.statusLabel_Disabled)}
                onChange={(selectedOption) => formik.setFieldValue('status', selectedOption ? 'Enabled' : 'Disabled')}
                disabled={isReadOnlyUser}
              />
            </div>
          </MerchantGenericCard>
          <MerchantGenericCard>
            <div className='payToCardPopup_section'> 
              <div>
                <h3>{intl.formatMessage(captions.payToCardIban)}</h3>
                <p>{intl.formatMessage(captions.payToCardIbanDescription)}</p>
              </div>
              <PortalTextBlocked value={formik.values.subscriptionParams?.PayToCardSenderIBAN} noPadding />
            </div>
          </MerchantGenericCard>
          <MerchantGenericCard>
            <div className='payToCardPopup_section'>
              <div>
                <h3>{intl.formatMessage(captions.payToCardVerificationTitle)}</h3>
                <p>{intl.formatMessage(captions.payToCardVerificationDescription)}</p>
              </div>
              <div ref={matchLevelSelectRef} className='payToCardPopup_matchLevel'>
                <PortalSelect
                  id='payToCard_matchLevel'
                  label={intl.formatMessage(captions.payToCardMatchLevel)}
                  ariaLabelIconButton={intl.formatMessage(captions.payToCardMatchLevel)}
                  value={formik?.values?.subscriptionParams?.PayToCardNameMatchScore || 'full'}
                  options={[
                    { value: 'full', label: intl.formatMessage(captions.payToCardExactMatch) },
                    { value: 'partial', label: intl.formatMessage(captions.payToCardPartialMatch) },
                    { value: 'nomatch', label: intl.formatMessage(captions.payToCardNoVerification) }
                  ]}
                  onChange={(option) => formik.setFieldValue('subscriptionParams.PayToCardNameMatchScore', option.value)}
                  onBlur={() => formik.setFieldTouched('subscriptionParams.PayToCardNameMatchScore', true)}
                  noSearch
                  disabled={formik.values.status === 'Disabled' || isReadOnlyUser}
                />
              </div>
            </div>
          </MerchantGenericCard>
        </div>
      </MerchantModal>
    </div>
  )
}

PayToCardPopup.propTypes = {
  actions: PropTypes.object,
  data: PropTypes.object,
  isUpdatingVas: PropTypes.bool,
  userPermissions: PropTypes.array,
}

PayToCardPopup.defaultProps = {
  actions: {},
  data: {},
  isUpdatingVas: false,
  userPermissions: [],
}

export default PayToCardPopup
